import { Building2, Plane, Hotel, Compass, Users, Share2, Landmark } from 'lucide-react';

export const COLLABORATE_DISCLOSURE =
  'Commercial terms, discounts, commissions, promotional codes and eligibility criteria are configured privately for each approved partner and are never published publicly. Any entitlements are drawn from governed account configuration once a partnership agreement is in place.';

export const PARTNERSHIP_ORDER = [
  'corporate', 'travel-agency', 'hotel-hospitality', 'airline-aviation', 'dmc-tour-operator', 'affiliate-referral', 'strategic',
];

export const PARTNERSHIPS = {
  'corporate': {
    icon: Building2,
    title: 'Corporate Partnerships',
    short: 'Mobility programmes for employees, business travellers and operations.',
    image: '/generated/leasing-fleet-corporate.jpg',
    lead: 'A single mobility partner for your people and your operations, from executive travel to everyday fleet needs, delivered under governed corporate accounts.',
    whoFor: [
      'HR, admin and procurement teams managing employee mobility',
      'Travel managers coordinating business travel and ground transport',
      'Operations leaders running commercial or field fleets',
      'Finance teams moving from vehicle ownership to predictable opex',
    ],
    problems: [
      'Fragmented suppliers across rental, chauffeur, leasing and coaches',
      'Unpredictable vehicle running costs and administrative overhead',
      'Inconsistent traveller experience and duty-of-care gaps',
      'No consolidated reporting or single point of accountability',
    ],
    products: ['Corporate car rental & monthly mobility', 'Operational vehicle leasing', 'Executive & airport chauffeur', 'Staff & group coach transport', 'Commercial vehicle & fleet solutions'],
    integrations: ['Consolidated corporate account & billing', 'Priority booking channels for authorised users', 'Governed rate configuration per account', 'Usage and utilisation reporting'],
    journey: ['Discovery & needs review', 'Programme design & governed configuration', 'Onboarding & authorised users', 'Live bookings across brands', 'Reviews & optimisation'],
    caseStudyLabel: 'Corporate mobility programme',
    caseStudy: [
      'Challenge: consolidate multiple mobility suppliers under one accountable partner.',
      'Approach: a blended programme across rental, leasing, chauffeur and coaches with a single account team.',
      'Outcome framework: measured on cost predictability, traveller satisfaction and administrative time saved.',
    ],
  },
  'travel-agency': {
    icon: Compass,
    title: 'Travel Agency Partnerships',
    short: 'Rental access, customer booking pathways and travel-mobility support.',
    image: '/brand/woman-phone-car.jpg',
    lead: 'Extend trusted UAE ground mobility to your travellers with rental access, chauffeur and transfer pathways that complement your itineraries.',
    whoFor: [
      'Retail and online travel agencies',
      'Corporate travel management companies',
      'Concierge and lifestyle travel desks',
      'Booking platforms adding ground-mobility options',
    ],
    problems: [
      'No reliable local ground-transport partner for arriving travellers',
      'Manual, off-system transfer and rental arrangements',
      'Difficulty offering a consistent premium experience on the ground',
      'Missed ancillary revenue on the mobility leg of the trip',
    ],
    products: ['Self-drive car rental', 'Airport transfers & meet-and-greet', 'Chauffeur & premium transport', 'Group & coach transfers'],
    integrations: ['Agency booking pathways for staff', 'Traveller booking links & referral routing', 'Coordinated airport pickups', 'Governed agency rate configuration'],
    journey: ['Agency onboarding', 'Booking pathway setup', 'Traveller quote & confirm', 'On-ground delivery', 'Post-trip reconciliation'],
    caseStudyLabel: 'Travel agency ground-mobility',
    caseStudy: [
      'Challenge: give travellers a dependable, branded ground-transport option.',
      'Approach: agency-linked booking pathways spanning rental, transfers and chauffeur.',
      'Outcome framework: measured on attachment rate, traveller feedback and repeat bookings.',
    ],
  },
  'hotel-hospitality': {
    icon: Hotel,
    title: 'Hotel & Hospitality Partnerships',
    short: 'Guest transfers, chauffeur services, car rental, staff transportation and visitor mobility.',
    image: '/brand/cover-limousine-dubai.jpg',
    lead: 'End-to-end guest and operational mobility for hotels, resorts and serviced residences, from arrival transfers to staff transport.',
    whoFor: [
      'Hotels, resorts and serviced apartments',
      'Guest relations, concierge and front-office teams',
      'Hospitality operations and HR managing staff transport',
      'Events and banqueting teams handling visitor movement',
    ],
    problems: [
      'Inconsistent guest transfer quality and availability',
      'Reliance on ad-hoc third-party drivers',
      'Complex staff transportation logistics across shifts',
      'No single partner for guest, staff and event mobility',
    ],
    products: ['Guest airport transfers', 'Chauffeur & limousine service', 'On-site guest car rental', 'Staff transportation', 'Event & group coach transfers'],
    integrations: ['Concierge booking channel', 'Guest booking links & folio-friendly billing', 'Dedicated staff-transport scheduling', 'Governed property rate configuration'],
    journey: ['Property onboarding', 'Service & schedule design', 'Concierge / guest booking', 'Live guest & staff delivery', 'Service reviews'],
    caseStudyLabel: 'Hospitality guest & staff mobility',
    caseStudy: [
      'Challenge: raise guest transfer quality while simplifying staff transport.',
      'Approach: one partner across chauffeur, transfers, rental and staff coaches.',
      'Outcome framework: measured on guest satisfaction, on-time performance and operational simplicity.',
    ],
  },
  'airline-aviation': {
    icon: Plane,
    title: 'Airline & Aviation Partnerships',
    short: 'Crew, passenger, airport and operational transport solutions.',
    image: '/generated/chauffeur-hero-door.jpg',
    lead: 'Reliable, compliance-minded ground transport for crews, passengers and airport operations across the UAE.',
    whoFor: [
      'Airlines and their crew-logistics teams',
      'Ground-handling and airport operations',
      'Aviation service providers and lounges',
      'VIP and premium passenger services',
    ],
    problems: [
      'Time-critical crew movements requiring dependable transport',
      'Fluctuating passenger transfer volumes',
      'Need for airside/landside operational vehicle support',
      'Premium passenger experience expectations',
    ],
    products: ['Crew transport', 'Passenger transfers & meet-and-greet', 'Chauffeur & VIP transport', 'Operational vehicle leasing', 'Group coach transfers'],
    integrations: ['Scheduled crew-movement planning', 'Priority dispatch channels', 'Consolidated operational billing', 'Governed aviation rate configuration'],
    journey: ['Requirement mapping', 'Schedule & SLA design', 'Onboarding & dispatch setup', 'Live crew & passenger delivery', 'Performance reviews'],
    caseStudyLabel: 'Aviation ground transport',
    caseStudy: [
      'Challenge: dependable, time-critical crew and passenger movements.',
      'Approach: scheduled crew transport plus flexible passenger and VIP capacity.',
      'Outcome framework: measured on on-time performance, reliability and passenger experience.',
    ],
  },
  'dmc-tour-operator': {
    icon: Users,
    title: 'DMC & Tour Operator Partnerships',
    short: 'Self-drive, chauffeur, group and tour-support mobility.',
    image: '/generated/coach-staff-boarding.jpg',
    lead: 'Flexible mobility capacity for destination management companies and tour operators, from self-drive to full group programmes.',
    whoFor: [
      'Destination management companies (DMCs)',
      'Inbound and outbound tour operators',
      'Excursion and activity operators',
      'MICE and group-travel organisers',
    ],
    problems: [
      'Scaling transport capacity for variable group sizes',
      'Coordinating multi-day, multi-vehicle itineraries',
      'Balancing self-drive and chauffeured options',
      'Maintaining service quality across peak seasons',
    ],
    products: ['Self-drive car rental', 'Chauffeur & guided transport', 'Group & coach transport', 'Airport & inter-city transfers'],
    integrations: ['Programme & itinerary planning', 'Group booking pathways', 'Coordinated multi-vehicle dispatch', 'Governed operator rate configuration'],
    journey: ['Operator onboarding', 'Itinerary & capacity planning', 'Group booking & confirmation', 'On-tour delivery', 'Season reviews'],
    caseStudyLabel: 'Tour & group mobility',
    caseStudy: [
      'Challenge: scale reliable transport across variable group programmes.',
      'Approach: blended self-drive, chauffeur and coach capacity with itinerary planning.',
      'Outcome framework: measured on capacity fulfilment, punctuality and guest experience.',
    ],
  },
  'affiliate-referral': {
    icon: Share2,
    title: 'Affiliate & Referral Partnerships',
    short: 'Tracked customer referrals and campaign partnerships.',
    image: '/brand/handover-couple.jpg',
    lead: 'Turn your audience into value with tracked referral and campaign partnerships across our mobility brands.',
    whoFor: [
      'Content creators, publishers and communities',
      'Comparison sites and booking aggregators',
      'Marketing and campaign partners',
      'Businesses with complementary audiences',
    ],
    problems: [
      'No structured way to monetise mobility-interested audiences',
      'Untracked referrals and unclear attribution',
      'Lack of campaign-ready assets and pathways',
      'Difficulty partnering with a trusted local operator',
    ],
    products: ['Referral routing across all brands', 'Campaign & seasonal partnerships', 'Co-marketing pathways', 'Tracked booking links'],
    integrations: ['Tracked referral links & attribution', 'Campaign landing pathways', 'Governed referral configuration', 'Performance reporting'],
    journey: ['Application & review', 'Tracking & assets setup', 'Campaign launch', 'Referrals & bookings', 'Performance reconciliation'],
    caseStudyLabel: 'Referral & campaign partnership',
    caseStudy: [
      'Challenge: monetise a mobility-interested audience with clear attribution.',
      'Approach: tracked referral links and co-marketing campaigns across brands.',
      'Outcome framework: measured on tracked referrals, conversion and campaign performance.',
    ],
  },
  'strategic': {
    icon: Landmark,
    title: 'Strategic Partnerships',
    short: 'Banks, insurers, retailers, loyalty programmes, property groups and digital platforms.',
    image: '/brand/control-room-dubai.jpg',
    lead: 'Embed mobility into your customer proposition, from loyalty benefits to platform integrations, through a governed strategic partnership.',
    whoFor: [
      'Banks, insurers and financial-services providers',
      'Retailers and loyalty-programme operators',
      'Property groups and community operators',
      'Digital platforms and super-apps',
    ],
    problems: [
      'Adding mobility value to an existing customer base',
      'Enriching loyalty and member benefit programmes',
      'Integrating mobility into a digital platform',
      'Finding a governed, enterprise-grade local partner',
    ],
    products: ['Member & loyalty mobility benefits', 'Bundled rental, leasing & chauffeur offers', 'Platform & API-style booking pathways', 'Community & property mobility programmes'],
    integrations: ['Governed benefit configuration', 'Co-branded booking pathways', 'Platform integration possibilities', 'Consolidated partnership reporting'],
    journey: ['Strategic scoping', 'Commercial & integration design', 'Governed configuration & onboarding', 'Launch & activation', 'Joint performance reviews'],
    caseStudyLabel: 'Strategic mobility integration',
    caseStudy: [
      'Challenge: add differentiated mobility value to an existing customer base.',
      'Approach: governed, co-branded programmes and platform booking pathways.',
      'Outcome framework: measured on member engagement, activation and partnership value.',
    ],
  },
};

export const COLLABORATE_HERO = {
  headline: 'Better mobility partnerships start with more possibilities.',
  supporting: 'Whether you serve travellers, guests, employees, members, customers, or communities, EGMG can help you create mobility experiences that add practical value to your business.',
};
